import {
  useRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Pagination, Table, Tag, Tooltip, Whisper } from "rsuite";
import { ApiPatient } from "../../core/apis";
import { ApiSchemaPatient } from "../../models/api/patient";
import { useHistory } from "react-router-dom";
import { AppRouteUi } from "../../config/appRoute";
import { ActionsPatientTable, useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import {
  ActionCell,
  DateCell,
  SeeTasksCell,
  TextCell,
} from "../../components/tableItem.component";
import { useAlertDialog } from "../../components/alertDialog";
import { sortTableData } from "../../utils/tableFilter";
import { DHS_INSURANCE_NAME } from "../../config/constants";
import { convertToTwoDecimal } from "../../utils/convertToTwoDecimal";
import { TableHeader } from "../../components";
import { FaUser, FaClock, FaRegCalendar, FaCalendarWeek, FaRegCalendarAlt, FaCog, FaUserPlus, FaUserEdit } from "react-icons/fa";
import { FiEye, FiEdit, FiTrash2, FiPower } from 'react-icons/fi';
import { UtilityDate } from '../../utils/dateUtils';

const { Column, HeaderCell, Cell } = Table;

type TableData = Omit<ApiSchemaPatient, "totalHours"> & {
  checked?: boolean;
  name?: string;
  billAmount: number | string;
  totalHours: string | number;
};

type TypeTableKey = keyof TableData;

type PatientListProps = {
  isValidating?: boolean;
  data?: ApiSchemaPatient[];
  isInProfile?: boolean;
  totalCount?: number;
};

export const PatientList = ({
  data,
  isValidating,
  isInProfile,
  totalCount,
}: PatientListProps) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selected, setSelected] = useState<TableData[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const triggerRef = useRef<any>();
  const routerHistory = useHistory();
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();
  const { pageNumber, pageSize, isLoading } = useAppSelector(
    (state) => state.patientTable
  );
  const dispatch = useAppDispatch();
  const patientFee = useAppSelector((state) => state.company.company?.rate);

  const [sortBy, setSortBy] = useState<TypeTableKey>("" as TypeTableKey);
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(
    () =>
      setTableData(
        data?.map((patient) => ({
          name: patient.firstName + " " + patient.lastName,
          caregiverName: patient.caregiverNames?.join(" , "),
          billAmount: convertToTwoDecimal(
            patient.totalUnit && patient.perUnitCost
              ? ( patient.isCostMeasureTypeHourly 
                ? patient.totalUnit * patient.perUnitCost : patient.perUnitCost * ((patient.totalUnit * 15) / 60) )
              : 0
          ),
          totalHours: patient.totalUnit
            ? (patient.isCostMeasureTypeHourly 
              ? patient.totalUnit :((patient.totalUnit * 15) / 60).toFixed(2))
            : 0,
          ...patient,
          totalUnit: patient.totalUnit 
            ? (patient.isCostMeasureTypeHourly 
              ? 0 : patient.totalUnit)
            : 0,
        })) ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const CellRowSelection = useMemo((): ReactNode => {
    return (rowData: TableData) => (
      <input
        type="checkbox"
        checked={!!rowData.checked}
        onChange={(ev) =>
          rowData.sKey &&
          setTableData((tableData) =>
            tableData?.map((td) =>
              td.sKey === rowData.sKey
                ? {
                    ...td,
                    checked: ev.target.checked,
                  }
                : td
            )
          )
        }
      />
    );
  }, []);

  const selectAllRow = useCallback((checked: boolean) => {
    setTableData((tableData) =>
      tableData.map((td) => ({
        ...td,
        checked: checked,
      }))
    );
  }, []);

  const isAllChecked = useMemo(
    () => tableData.length > 0 && tableData.every((td) => !!td.checked),
    [tableData]
  );

  useEffect(() => {
    setSelected(tableData.filter((td) => !!td.checked));
  }, [tableData]);

  const updateDeleted =
    (sKeys: string[]) => async (res?: ApiSchemaPatient[]) => {
      return res?.filter((i) => !sKeys.includes(i.sKey)) || [];
    };

  const onDelete = useCallback(
    () =>
      alertDialogCtrl.open({
        message: "Are you sure you want to delete the selected Patients?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          setIsDeleting(true);
          dispatch(ActionsPatientTable.setIsLoading(true));
          const selectedIds = selected.filter((s) => s.sKey).map((s) => s.sKey);
          await deleteItem(selectedIds);
          const sKeys = selected.map((item) => item.sKey);
          ApiPatient.GetAll.Mutate(updateDeleted(sKeys));
          setIsDeleting(false);
          dispatch(ActionsPatientTable.setIsLoading(false));
          dispatch(ActionsPatientTable.setRefetch(true));
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const handleSelectMenu = async (
    eventKey: string,
    event: Event,
    sKey?: string,
    rowData?: any,
    isDeActivated?: boolean
  ) => {
    if (eventKey === "delete" && sKey)
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this Patient?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsPatientTable.setIsLoading(true));
          const deleted = await deleteItem([sKey]);
          if (deleted) ApiPatient.GetAll.Mutate(updateDeleted([sKey]));
          dispatch(ActionsPatientTable.setIsLoading(false));
        },
      });
    else if (eventKey === "changeStatus" && sKey) {
      alertDialogCtrl.open({
        message: `Are you sure you want to ${
          isDeActivated ? "Activate" : "Deactivate"
        } this Patient?`,
        body: !isDeActivated ? (
          <p className="text-red-300">
            If you deactivate this patient, appointments associated with this
            patient won't work. Do you want to deactivate?
          </p>
        ) : !rowData?.chargeRequiredForActivation ? (
          ""
        ) : (
          <>
            <p>
              Per patient fee is ({" "}
              <span className="fw-bold">$ {patientFee}</span> per month )
            </p>
            <p className="text-muted badge bg-light fw-normal mt-4">
              By selecting "Activate", you acknowledge and agree to purchase an
              additional patient license.
            </p>
          </>
        ),
        proceedBtnText: isDeActivated ? "Activate" : "Deactivate",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsPatientTable.setIsLoading(true));
          await ApiPatient.ActivateDeactivate(sKey, !isDeActivated!);
          dispatch(ActionsPatientTable.setRefetch(true));
          dispatch(ActionsPatientTable.setIsLoading(false));
        },
      });
    } else if (eventKey === "update" && sKey)
      routerHistory.push(AppRouteUi.Patient.UpdateById(sKey));
  };

  const deleteItem = async (sKey: string[]) => await ApiPatient.Delete(sKey);

  const filteredTableData = useMemo(() => {
    return sortTableData(tableData, sortBy, sortAsc);
  }, [tableData, sortBy, sortAsc]);

  return (
    <div>
      {alertDialogComponent}
      <TableHeader
        buttonText="Add New Patient"
        redirectPath={AppRouteUi.Patient.Add()}
        onDelete={onDelete}
        pageSize={pageSize}
        TableAction={ActionsPatientTable}
        isDeleting={isDeleting}
        isSelected={!!selected.length}
      />
      <Table
        autoHeight
        data={
          !totalCount
            ? filteredTableData.filter(
                (val, idx) =>
                  idx >= (pageNumber - 1) * pageSize &&
                  idx < pageNumber * pageSize
              )
            : filteredTableData
        }
        loading={isValidating || isLoading}
        onScroll={() => triggerRef.current?.close()}
        rowClassName={(rowData: any) =>
          new Date(rowData?.serviceAuthorizationEndDate) < new Date()
            ? 'conflict-table-row table-row-hover'
            : 'table-row-hover'
        }
        bordered
        cellBordered
        hover
        classPrefix="rs-table"
        className="table-striped"
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Patients Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
      >
        <Column flexGrow={0.1}>
          <HeaderCell className="fw-bold text-black">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={(ev) => selectAllRow(ev.target.checked)}
            />
          </HeaderCell>
          <Cell>{CellRowSelection}</Cell>
        </Column>

        <Column flexGrow={0.6} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaUser className="me-2" /> Patient
            </span>
          </HeaderCell>
          <TextCell component="Patient" dataKey="name" />
        </Column>

        <Column flexGrow={0.6} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaUser className="me-2" /> Caregiver
            </span>
          </HeaderCell>
          <TextCell component="Patient" dataKey="caregiverName" />
        </Column>

        <Column flexGrow={0.4} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaClock className="me-2" /> T.Units
            </span>
          </HeaderCell>
          <Cell dataKey="totalUnit">
            {(rowData: ApiSchemaPatient) => `${rowData.totalUnit}`}
          </Cell>
        </Column>

        <Column flexGrow={0.4} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaClock className="me-2" /> T.Hours
            </span>
          </HeaderCell>
          <Cell dataKey="totalHours">
            {(rowData: ApiSchemaPatient) => `${rowData.totalHours}`}
          </Cell>
        </Column>

        <Column flexGrow={0.4} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Service Authorization start date">Auth. Start</span>
          </HeaderCell>
          <Cell dataKey="serviceAuthorizationStartDate">
            {(rowData: any) => (
              <span>
                {UtilityDate.formatHumanFriendlyDate(new Date(rowData.serviceAuthorizationStartDate))}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.4} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Service Authorization end date">Auth. End</span>
          </HeaderCell>
          <Cell dataKey="serviceAuthorizationEndDate">
            {(rowData: any) => (
              <span>
                {UtilityDate.formatHumanFriendlyDate(new Date(rowData.serviceAuthorizationEndDate))}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.6}>
          <HeaderCell className="fw-bold text-black">
            <span title="Case Manager">Case Manager</span>
          </HeaderCell>
          <TextCell component="Patient" dataKey="caseManager" />
        </Column>

        <Column>
          <HeaderCell className="fw-bold text-black">
            <span title="Invoice Type">Invoice Type</span>
          </HeaderCell>
          <Cell dataKey="invoiceGenerationInvokeType">
            {(rowData: TableData) => {
              const type = rowData.invoiceGenerationInvokeType;
              const getIcon = () => {
                switch (type) {
                  case "Daily":
                    return <FaRegCalendar className="text-primary" />;
                  case "Weekly":
                    return <FaCalendarWeek className="text-success" />;
                  case "Biweekly":
                    return <FaCalendarWeek className="text-warning" />;
                  case "Monthly":
                    return <FaRegCalendarAlt className="text-info" />;
                  default:
                    return <FaCog />;
                }
              };
              return (
                <div className="d-flex align-items-center">
                  {getIcon()}
                  <span className="ms-2">{type}</span>
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={0.3} align="center">
          <HeaderCell className="fw-bold text-black">
            <span title="Transport">Transport</span>
          </HeaderCell>
          <Cell>
            {({ isTransportAvailable }: TableData) => (
              <Tag>
                {isTransportAvailable ? (
                  <i className="fa fa-check" style={{ color: "green" }} />
                ) : (
                  <i className="fa fa-times" style={{ color: "red" }} />
                )}
              </Tag>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.3}>
          <HeaderCell className="fw-bold text-black">
            <span title="Task List">Task List</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <SeeTasksCell
                taskDetailsList={rowData.taskDetailsList}
                isDHS={rowData.insuranceTypeName === DHS_INSURANCE_NAME}
              />
            )}
          </Cell>
        </Column>

        {data?.[0] && !isInProfile && (
          <Column flexGrow={0.3}>
            <HeaderCell className="fw-bold text-black">
              <span title="Caregiver">
                <FaUser className="me-1" /> Assign
              </span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) => (
                <Whisper
                  trigger="hover"
                  placement="left"
                  speaker={
                    <Tooltip>
                      {rowData.caregiverNames?.length ? "Update Caregiver Assignment" : "Add Caregiver Assignment"}
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      routerHistory.push(AppRouteUi.Appointment.Add(), {
                        patientSKey: rowData.sKey,
                      })
                    }
                    className="btn btn-icon btn-sm"
                    style={{ color: '#198754' }}
                  >
                    {rowData.caregiverNames?.length ? 
                      <FaUserEdit size={18} /> : 
                      <FaUserPlus size={18} />
                    }
                  </button>
                </Whisper>
              )}
            </Cell>
          </Column>
        )}

        <Column align="center" flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div className="d-flex align-items-center justify-content-center gap-2">
                <button 
                  className="btn btn-sm btn-outline-info"
                  onClick={() => routerHistory.push(AppRouteUi.Patient.ByIdV2(rowData.sKey))}
                  title="View"
                >
                  <FiEye />
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => routerHistory.push(AppRouteUi.Patient.UpdateById(rowData.sKey!))}
                  title="Edit"
                >
                  <FiEdit />
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleSelectMenu('delete', new Event('click'), rowData.sKey)}
                  title="Delete"
                >
                  <FiTrash2 />
                </button>
                <button
                  className="btn btn-sm btn-outline-warning"
                  onClick={() => handleSelectMenu('changeStatus', new Event('click'), rowData.sKey, null, rowData.isDeActivated)}
                  title={rowData.isDeActivated ? 'Activate' : 'Deactivate'}
                >
                  <FiPower />
                </button>
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      {data && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={filteredTableData.length}
            pages={
              totalCount
                ? Math.ceil(totalCount / pageSize)
                : Math.ceil(filteredTableData.length / pageSize)
            }
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page: any) =>
              dispatch(ActionsPatientTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
};
